import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import MemberForm, { IMemberFormData, MemberGroupChanges } from '../../components/forms/MemberForm';
import GroupChangePrompts, { type GroupChangeOp, groupChangeOps } from '../../components/GroupChangePrompts';
import MemberMemberships from '../../components/MemberMemberships';
import MemberPayments from '../../components/MemberPayments';
import CollapsibleWidget from '../../components/CollapsibleWidget';
import RoleAccessWidget from '../../components/RoleAccessWidget';
import Button from '../../components/ui/Button';
import { translateError } from '../../utils/errorMessages';

interface IGroupDisplay {
  _id: string;
  name: string;
}

interface IUserDisplay {
  _id: string;
  name: string;
  email: string;
}

interface IMember {
  _id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  description: string;
  user?: IUserDisplay | string;
  groups: IGroupDisplay[] | string[];
  activityIds?: number[];
  dateOfBirth?: string;
  city?: string;
  cityDistrict?: string;
  nationality?: string;
  birthNumber?: string;
  street?: string;
  zipCode?: string;
  buildingNumber?: string;
  streetNumber?: string;
  allergies?: string;
  medicalNotes?: string;
  email?: string;
  phone?: string;
  color?: string;
  /** Backend stores the address as a jsonb object; flat fields above are legacy. */
  address?: {
    city?: string;
    cityDistrict?: string;
    street?: string;
    zipCode?: string;
    buildingNumber?: string;
    streetNumber?: string;
  } | null;
  /** Backend stores the emergency contact as a jsonb object. */
  emergencyContact?: {
    name?: string;
    phone?: string;
    relationship?: string;
  } | null;
  /** Set when the member is a tombstone (soft-deleted). */
  deletedAt?: string | null;
}

const MemberEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const [member, setMember] = useState<IMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'form' | 'payments'>('form');
  const [paymentsRefreshKey, setPaymentsRefreshKey] = useState(0);
  const [restoring, setRestoring] = useState(false);
  const [groupOps, setGroupOps] = useState<GroupChangeOp[] | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMember = async () => {
      try {
        setLoading(true);
        const { data } = await api.members.get(id!);
        setMember(data as IMember);
        setLoading(false);
      } catch (err: any) {
        setError(translateError(err, t));
        setLoading(false);
        if (err.response && err.response.status === 404) {
          navigate('/admin/members'); // Go back to list if member not found
        }
      }
    };

    if (id) {
      fetchMember();
    }
  }, [id, navigate]);

  const handleRestore = async () => {
    if (!id) return;
    setRestoring(true);
    try {
      await api.members.restore(id);
      const { data } = await api.members.get(id);
      setMember(data as IMember);
      addToast(t('dataSafety.restoreSuccess'), 'success');
    } catch (err: any) {
      // Toast only: `error` replaces the whole page below, which would take the
      // restore button with it and leave no way to retry.
      addToast(t('common.error') + ': ' + translateError(err, t), 'error');
    } finally {
      setRestoring(false);
    }
  };

  const submitHandler = async (data: IMemberFormData, groupChanges: MemberGroupChanges) => {
    setLoading(true);
    setError(null);

    try {
      await api.members.update(id!, data);
      setLoading(false);
      addToast(t('members.editTitle') + ' - ' + t('common.save') + '!', 'success');
      // Group changes go through the D6 prompt; leave once it is done.
      const ops = groupChangeOps(Number(id), groupChanges);
      if (ops.length > 0) setGroupOps(ops);
      else navigate('/admin/members');
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
      setLoading(false);
    }
  };

  if (loading && !member) return <p>{t('members.loadingMember')}</p>;
  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  // Prepare initial data for the form
  const initialData = member ? {
    name: member.name,
    firstName: member.firstName,
    lastName: member.lastName,
    description: member.description,
    user: member.user ? String(typeof member.user === 'string' ? member.user : member.user._id) : '',
    groups: member.groups.map(g => typeof g === 'string' ? g : g._id),
    activityIds: member.activityIds ?? [],
    dateOfBirth: member.dateOfBirth ? member.dateOfBirth.split('T')[0] : undefined,
    // Address is stored as a jsonb object — unfold it into the form's flat fields.
    city: member.address?.city ?? member.city,
    cityDistrict: member.address?.cityDistrict ?? member.cityDistrict,
    nationality: member.nationality,
    birthNumber: member.birthNumber,
    street: member.address?.street ?? member.street,
    zipCode: member.address?.zipCode ?? member.zipCode,
    buildingNumber: member.address?.buildingNumber ?? member.buildingNumber,
    streetNumber: member.address?.streetNumber ?? member.streetNumber,
    allergies: member.allergies,
    medicalNotes: member.medicalNotes,
    // Seeded so the editor shows what is stored -- and so orgs that mark
    // email/phone required can still save an existing member.
    email: member.email,
    phone: member.phone,
    color: member.color,
    emergencyContact: member.emergencyContact ?? null,
  } : undefined;

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

  return (
    <div className="admin-page-content">
      <h2>{t('members.editTitle')}</h2>

      {member?.deletedAt && (
        <div className="tombstone-banner">
          <p className="tombstone-banner__text">{t('dataSafety.deletedBanner')}</p>
          <Button variant="secondary" size="sm" onClick={handleRestore} loading={restoring}>
            {t('dataSafety.restoreAction')}
          </Button>
        </div>
      )}

      {/* Mobile Tab Switcher */}
      <div className="tab-switcher mobile-only">
        <button
          className={activeTab === 'form' ? 'active' : ''}
          onClick={() => setActiveTab('form')}
        >
          {t('members.tabEdit')}
        </button>
        <button
          className={activeTab === 'payments' ? 'active' : ''}
          onClick={() => setActiveTab('payments')}
        >
          {t('members.tabPayments')}
        </button>
      </div>

      <GroupChangePrompts
        ops={groupOps}
        memberNames={member ? { [String(member._id)]: member.name } : {}}
        onDone={() => {
          setGroupOps(null);
          navigate('/admin/members');
        }}
      />

      <div className="edit-page-layout">
        <div className={`edit-page-form ${activeTab === 'form' ? 'active' : ''}`}>
          <MemberForm
            initialData={initialData}
            seedKey={member?._id}
            onSubmit={submitHandler}
            loading={loading && !!member} // Only show button loading state when updating
            submitButtonText={t('members.updateButton')}
          />
        </div>

        <div className={`edit-page-widgets ${activeTab === 'payments' ? 'active' : ''}`}>
          {id && (
            <>
              <CollapsibleWidget
                title={t('memberships.title')}
                defaultExpanded={true}
                storageKey={`member-${id}-memberships`}
              >
                <MemberMemberships
                  memberId={id}
                  authToken={userInfo.token}
                  onTierChange={() => setPaymentsRefreshKey(k => k + 1)}
                />
              </CollapsibleWidget>
              <CollapsibleWidget
                title={t('payments.title')}
                defaultExpanded={true}
                storageKey={`member-${id}-payments`}
              >
                <MemberPayments
                  memberId={id}
                  memberName={member?.name || ''}
                  refreshKey={paymentsRefreshKey}
                />
              </CollapsibleWidget>
              <CollapsibleWidget
                title={t('roles.entityAccess')}
                defaultExpanded={false}
                storageKey={`member-${id}-role-access`}
              >
                <RoleAccessWidget entityType="members" entityId={id} />
              </CollapsibleWidget>
            </>
          )}
        </div>
      </div>

      <Link to="/admin/members" className="form-link">{t('members.goBack')}</Link>
    </div>
  );
};

export default MemberEditPage;